import React, { useRef, useEffect, useState, useCallback } from 'react';

// Selettore strumenti a "ruota": la cornice di selezione resta FISSA al centro; sono le icone
// a scorrere. Quella che si aggancia al centro (snap) e' quella attiva. Swipe PER scorrere e
// TAP su un'icona per selezionarla direttamente (utile con poche voci: la ruota non scorre).
// La centrale e' colorata + etichetta; le laterali attenuate.
export default function TabWheel({ tabs, value, onChange, lockedTo, hint, haptic }) {
  const ref = useRef(null);
  const [center, setCenter] = useState(0);
  const [hand, setHand] = useState({ x: 0, op: 1 });   // posizione mano-tutorial, legata allo scroll
  const settleTO = useRef(null);
  const lastCenter = useRef(-1);   // per il feedback aptico: vibra solo quando cambia l'icona centrata
  // Tic di vibrazione (Android): su iOS navigator.vibrate non esiste -> no-op silenzioso.
  const buzz = (ms) => { if (haptic) { try { if (navigator.vibrate) navigator.vibrate(ms); } catch { /* no-op */ } } };

  // Mano del tutorial: la sposto in base alla posizione REALE di scorrimento, cosi' segue il
  // selettore. Ogni icona che passa = una spazzata del dito (con dissolvenza sul "salto").
  const aggiornaMano = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const iw = (el.querySelector('.twi') && el.querySelector('.twi').offsetWidth) || 68;
    const off = (((el.scrollLeft % iw) + iw) % iw) / iw;   // 0..1 dentro un'icona
    setHand({ x: (0.5 - off) * 64, op: 1 - Math.min(1, Math.abs(0.5 - off) * 1.8) * 0.82 });
  }, []);

  const calcCenter = useCallback(() => {
    const el = ref.current;
    if (!el) return 0;
    const mid = el.scrollLeft + el.clientWidth / 2;
    let best = 0, bd = Infinity;
    el.querySelectorAll('.twi').forEach((it, i) => {
      const c = it.offsetLeft + it.offsetWidth / 2;
      const d = Math.abs(c - mid);
      if (d < bd) { bd = d; best = i; }
    });
    return best;
  }, []);

  const scrollToIndex = useCallback((i, smooth = true) => {
    const el = ref.current;
    if (!el) return;
    const it = el.querySelectorAll('.twi')[i];
    if (!it) return;
    el.scrollTo({ left: it.offsetLeft + it.offsetWidth / 2 - el.clientWidth / 2, behavior: smooth ? 'smooth' : 'auto' });
  }, []);

  // Tap diretto su un'icona: la porta al centro e la seleziona (rispetta il lock del dominio).
  // Non scrolla qui: cambiando 'value' e' l'effetto sotto a far scorrere la ruota in modo FLUIDO.
  const onPick = (i) => {
    const t = tabs[i];
    if (!t) return;
    if (lockedTo && t.key !== lockedTo) {
      const li = tabs.findIndex(x => x.key === lockedTo);
      if (li >= 0) scrollToIndex(li, true);   // torna fluido sul dominio
      return;
    }
    if (t.key !== value) { buzz(12); onChange(t.key); }   // il cambio 'value' scorre fluido (useEffect)
    else scrollToIndex(i, true);               // gia' attivo: ri-centra comunque, fluido
  };

  const onScroll = () => {
    const c = calcCenter();
    setCenter(c);
    if (c !== lastCenter.current) { lastCenter.current = c; buzz(9); }   // scatto rotella: tic aptico
    if (hint) aggiornaMano();
    clearTimeout(settleTO.current);
    settleTO.current = setTimeout(() => {   // assestato: aggiorna lo strumento attivo
      const t = tabs[calcCenter()];
      if (!t) return;
      if (lockedTo && t.key !== lockedTo) {   // dominio in corso: torna sul dominio
        const li = tabs.findIndex(x => x.key === lockedTo);
        if (li >= 0) scrollToIndex(li);
        return;
      }
      if (t.key !== value) onChange(t.key);
    }, 130);
  };

  // Posiziona la ruota sullo strumento corrente (all'avvio e se cambia da fuori).
  // rAF: aspetta il layout, cosi' all'apertura la ruota e' gia' centrata su 'Numero'.
  const mounted = useRef(false);
  useEffect(() => {
    const i = tabs.findIndex(x => x.key === value);
    if (i < 0) return undefined;
    setCenter(i);
    // Primo posizionamento (apertura): istantaneo, cosi' la ruota compare gia' centrata.
    // Cambi successivi (tap o esterni): scorrimento FLUIDO verso lo strumento scelto.
    const smooth = mounted.current || !!hint;
    const r = requestAnimationFrame(() => { scrollToIndex(i, smooth); mounted.current = true; });
    return () => cancelAnimationFrame(r);
  }, [value, tabs.length, scrollToIndex, hint]);

  return (
    <div className="tabwheel-wrap" data-tour="seg">
      <div className="tabwheel-sel" />
      <div className="tabwheel" ref={ref} onScroll={onScroll}>
        {tabs.map((t, i) => {
          const Ico = t.Ico;
          return (
            <div key={t.key} className={`twi t-${t.key}${i === center ? ' on' : ''}${t.busy ? ' busy' : ''}`}
              role="button" tabIndex={0} style={{ cursor: 'pointer' }}
              onClick={() => onPick(i)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPick(i); } }}>
              <Ico size={22} />
              <span>{t.label}</span>
              {t.dot && <span className="tabdot" />}
            </div>
          );
        })}
      </div>
      {hint && (
        <div className="tabwheel-hint" aria-hidden="true">
          <svg className="tabwheel-hand" viewBox="0 0 24 24" width="36" height="36" fill="currentColor"
            style={{ transform: `translateX(${hand.x}px)`, opacity: hand.op }}>
            <path d="M23 5.5V20c0 2.2-1.8 4-4 4h-7.3c-1.08 0-2.1-.43-2.85-1.19L1 14.83s1.26-1.23 1.3-1.25c.22-.19.49-.29.79-.29.22 0 .42.06.6.16.04.01 4.31 2.46 4.31 2.46V4c0-.83.67-1.5 1.5-1.5S13 3.17 13 4v7h1V1.5c0-.83.67-1.5 1.5-1.5S17 .67 17 1.5V11h1V2.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5V11h1V5.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5z" />
          </svg>
        </div>
      )}
    </div>
  );
}
